import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalPageLayout from '../components/LegalPageLayout';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resetConsent } from '../utils/consent';

const RECAPTCHA = Boolean(process.env.REACT_APP_RECAPTCHA_SITE_KEY);
const TRUSTPILOT = Boolean(process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID || process.env.REACT_APP_TRUSTPILOT_INVITE_KEY);

/**
 * The cookie policy lists what this store actually stores in the visitor's
 * browser, built from its settings: analytics only when a GA4 ID is set,
 * the Meta Pixel only when a Pixel ID is set, IutePay only when it's on.
 * Turning a service on or off in admin updates the page; nothing else to
 * maintain. "Change my choice" reopens the consent banner.
 */
const CookiePolicy = () => {
  const { t } = useTranslation();
  const { ga4MeasurementId, metaPixelId, iutepay, marketingConsentRequired } = useSiteSettings();

  const groups = [
    {
      key: 'necessary',
      rows: [
        ['cart', t('cookies.provider.store', 'This shop'), 'cart', t('cookies.until.emptied', 'Until you empty the cart')],
        ['i18nextLng', t('cookies.provider.store', 'This shop'), 'language', t('cookies.until.changed', 'Until you change it')],
        ['customer_token', t('cookies.provider.store', 'This shop'), 'signin', t('cookies.until.signout', 'Until you sign out')],
        marketingConsentRequired && ['marketing_consent', t('cookies.provider.store', 'This shop'), 'consent', t('cookies.until.changed', 'Until you change it')],
        ['__cf_bm', 'Cloudflare', 'security', t('cookies.duration.minutes', { count: 30, defaultValue: '30 minutes' })],
        RECAPTCHA && ['_GRECAPTCHA', 'Google reCAPTCHA', 'recaptcha', t('cookies.duration.months', { count: 6, defaultValue: '6 months' })],
      ],
    },
    iutepay && {
      key: 'payments',
      rows: [
        ['iutms', 'IuteCredit (IutePay)', 'iuteCampaign', t('cookies.duration.days', { count: 7, defaultValue: '7 days' })],
        ['checkoutSessionId', 'IuteCredit (IutePay)', 'iuteSession', t('cookies.until.next', 'Until your next application')],
      ],
    },
    ga4MeasurementId && {
      key: 'analytics',
      rows: [
        ['_ga, _ga_*', 'Google Analytics', 'analytics', t('cookies.duration.years', { count: 2, defaultValue: '2 years' })],
      ],
    },
    metaPixelId && {
      key: 'marketing',
      rows: [
        ['_fbp, _fbc', 'Meta (Facebook, Instagram)', 'meta', t('cookies.duration.months', { count: 3, defaultValue: '3 months' })],
      ],
    },
    TRUSTPILOT && {
      key: 'reviews',
      rows: [
        ['Trustpilot', 'Trustpilot', 'trustpilot', t('cookies.duration.provider', "Set by Trustpilot's own policy")],
      ],
    },
  ].filter(Boolean);

  const purpose = (key) => t(`cookies.purpose.${key}`, PURPOSES[key]);

  return (
    <LegalPageLayout
      namespace="cookies"
      sectionCount={3}
      after={(
        <div className="legal-body cookie-list">
          {groups.map((group) => (
            <section className="legal-section" key={group.key}>
              <h2>{t(`cookies.group.${group.key}.title`, GROUPS[group.key].title)}</h2>
              <p>{t(`cookies.group.${group.key}.body`, GROUPS[group.key].body)}</p>
              <div className="cookie-table-wrap">
                <table className="cookie-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('cookies.column.name', 'Name')}</th>
                      <th scope="col">{t('cookies.column.provider', 'Set by')}</th>
                      <th scope="col">{t('cookies.column.purpose', 'What it does')}</th>
                      <th scope="col">{t('cookies.column.duration', 'Kept for')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.filter(Boolean).map(([name, provider, key, duration]) => (
                      <tr key={name}>
                        <td><code>{name}</code></td>
                        <td>{provider}</td>
                        <td>{purpose(key)}</td>
                        <td>{duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          {marketingConsentRequired && (
            <section className="legal-section">
              <button type="button" className="cookie-settings-button" onClick={resetConsent}>
                {t('cookies.change', 'Change my cookie choice')}
              </button>
            </section>
          )}
        </div>
      )}
    />
  );
};

const GROUPS = {
  necessary: { title: 'Necessary', body: 'Needed for the shop to work: your cart, language, sign-in, security and spam protection at checkout. They are always on.' },
  payments: { title: 'Instalment payments (IutePay)', body: "IuteCredit's script shows monthly prices and runs the IutePay application. It sets these only when you use IutePay or arrive from one of IuteCredit's campaign links." },
  analytics: { title: 'Analytics', body: 'Help us understand which pages and products visitors use, so we can improve the shop. Counted without cookies until you accept, where we ask for your choice.' },
  marketing: { title: 'Marketing', body: 'Let us measure our Instagram and Facebook ads and show our products to people who visited the shop.' },
  reviews: { title: 'Reviews', body: "Trustpilot's widget shows our reviews and invites customers to review us after a purchase." },
};

const PURPOSES = {
  cart: 'Keeps the products in your cart',
  language: 'Remembers the language you chose',
  signin: 'Keeps you signed in to your account',
  consent: 'Remembers your cookie choice',
  security: 'Tells people from bots, to protect the shop',
  recaptcha: 'Protects the checkout and contact forms from spam',
  iuteCampaign: "Remembers the IuteCredit campaign link that brought you here",
  iuteSession: 'Keeps your IutePay application while it is open',
  analytics: 'Counts visits and how pages are used',
  meta: 'Measures our Meta ads and builds audiences for them',
  trustpilot: 'Shows reviews and sends review invitations',
};

export default CookiePolicy;
